import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, Building2, Gavel, FileText, TrendingUp, TrendingDown,
  Activity, BarChart3, PieChart, Calendar, RefreshCw, Loader2,
  CheckCircle, XCircle, Clock, Mail, Award, Target
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topAgents, setTopAgents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, agentsRes] = await Promise.all([
        axios.get('/admin/analytics/overview'),
        axios.get('/admin/analytics/trends?days=30'),
        axios.get('/admin/analytics/top-agents?limit=5')
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setTopAgents(agentsRes.data.top_agents);
    } catch (error) {
      toast.error('Errore nel caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Dati aggiornati');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = "text-[#0071e3]", subValue, subLabel }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
            {subValue !== undefined && (
              <p className="text-xs text-gray-400 mt-1">
                {subLabel}: <span className="font-medium">{subValue}</span>
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ProgressBar = ({ value, max, color = "bg-[#0071e3]" }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full ${color}`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  // Calculate totals for trend sparklines
  const getTrendTotal = (trendArray) => {
    if (!trendArray || trendArray.length === 0) return 0;
    return trendArray.reduce((sum, item) => sum + (item.count || item.total || 0), 0);
  };

  const getTrendChange = (trendArray) => {
    if (!trendArray || trendArray.length < 14) return 0;
    const recent = trendArray.slice(-7).reduce((sum, item) => sum + (item.count || item.total || 0), 0);
    const previous = trendArray.slice(-14, -7).reduce((sum, item) => sum + (item.count || item.total || 0), 0);
    if (previous === 0) return recent > 0 ? 100 : 0;
    return Math.round(((recent - previous) / previous) * 100);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0071e3]">Analytics Dashboard</h1>
          <p className="text-gray-500">Panoramica completa delle metriche della piattaforma</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* User Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" /> Utenti
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Proprietari" 
            value={overview?.total_users?.sellers || 0}
            icon={Users}
            color="text-blue-600"
            subValue={overview?.new_users_30d?.sellers || 0}
            subLabel="Ultimi 30gg"
          />
          <StatCard 
            title="Agenti" 
            value={overview?.total_users?.agents || 0}
            icon={Building2}
            color="text-green-600"
            subValue={overview?.new_users_30d?.agents || 0}
            subLabel="Ultimi 30gg"
          />
          <StatCard 
            title="Acquirenti" 
            value={overview?.total_users?.buyers || 0}
            icon={Users}
            color="text-purple-600"
            subValue={overview?.new_users_30d?.buyers || 0}
            subLabel="Ultimi 30gg"
          />
          <StatCard 
            title="Totale Utenti" 
            value={(overview?.total_users?.sellers || 0) + (overview?.total_users?.agents || 0) + (overview?.total_users?.buyers || 0)}
            icon={Activity}
            color="text-[#0071e3]"
          />
        </div>
      </div>

      {/* Property & Auction Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Immobili
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Totale immobili</span>
              <span className="font-bold text-xl">{overview?.property_stats?.total_seller_properties || 0}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-amber-500" /> In revisione
                </span>
                <span>{overview?.property_stats?.pending_review || 0}</span>
              </div>
              <ProgressBar 
                value={overview?.property_stats?.pending_review || 0} 
                max={overview?.property_stats?.total_seller_properties || 1}
                color="bg-amber-500"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-green-500" /> Approvati
                </span>
                <span>{overview?.property_stats?.approved || 0}</span>
              </div>
              <ProgressBar 
                value={overview?.property_stats?.approved || 0} 
                max={overview?.property_stats?.total_seller_properties || 1}
                color="bg-green-500"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <Gavel className="h-3 w-3 text-[#0071e3]" /> In gara
                </span>
                <span>{overview?.property_stats?.in_auction || 0}</span>
              </div>
              <ProgressBar 
                value={overview?.property_stats?.in_auction || 0} 
                max={overview?.property_stats?.total_seller_properties || 1}
                color="bg-[#0071e3]"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5" /> Aste
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{overview?.auction_stats?.active || 0}</p>
                <p className="text-sm text-gray-600">Attive</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{overview?.auction_stats?.scheduled || 0}</p>
                <p className="text-sm text-gray-600">Programmate</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">{overview?.auction_stats?.completed || 0}</p>
                <p className="text-sm text-gray-600">Completate</p>
              </div>
              <div className="text-center p-4 bg-[#0071e3]/10 rounded-lg">
                <p className="text-2xl font-bold text-[#0071e3]">{overview?.auction_stats?.total_bids || 0}</p>
                <p className="text-sm text-gray-600">Offerte totali</p>
              </div>
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Offerte ultimi 7 giorni</span>
                <Badge variant="outline" className="bg-[#0071e3]/10">
                  {overview?.auction_stats?.bids_last_7d || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversion & Proposals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Proposte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Totale</span>
                <span className="font-bold">{overview?.proposal_stats?.total_proposals || 0}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-amber-600">In attesa</span>
                <span>{overview?.proposal_stats?.pending || 0}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-green-600">Accettate</span>
                <span>{overview?.proposal_stats?.accepted || 0}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>Ultimi 30gg</span>
                <span>{overview?.proposal_stats?.proposals_30d || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" /> Conversioni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm">Proposta → Mandato</span>
                  <span className="font-bold text-[#0071e3]">{overview?.conversion_rates?.proposal_to_mandate || 0}%</span>
                </div>
                <ProgressBar value={overview?.conversion_rates?.proposal_to_mandate || 0} max={100} />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm">Immobile → Gara</span>
                  <span className="font-bold text-[#0071e3]">{overview?.conversion_rates?.property_to_auction || 0}%</span>
                </div>
                <ProgressBar value={overview?.conversion_rates?.property_to_auction || 0} max={100} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Verifica Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span>Proprietari verificati</span>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{overview?.email_verification?.sellers_verified || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-red-600">{overview?.email_verification?.sellers_unverified || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span>Agenti verificati</span>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{overview?.email_verification?.agents_verified || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-red-600">{overview?.email_verification?.agents_unverified || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span>Acquirenti verificati</span>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{overview?.email_verification?.buyers_verified || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-red-600">{overview?.email_verification?.buyers_unverified || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Agents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-[#0071e3]" /> Top Agenti
          </CardTitle>
          <CardDescription>Classificati per performance complessiva</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topAgents.map((agent, idx) => (
              <div key={agent.agent_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                    idx === 0 ? 'bg-[#0071e3]' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium">{agent.company_name || agent.name}</p>
                    <p className="text-xs text-gray-500">{agent.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="font-bold text-[#0071e3]">{agent.performance?.mandates_won || 0}</p>
                    <p className="text-xs text-gray-500">Mandati</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-green-600">{agent.performance?.acceptance_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Accettazione</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-purple-600">{agent.profile_completeness || 0}%</p>
                    <p className="text-xs text-gray-500">Profilo</p>
                  </div>
                  <Badge className="bg-[#0071e3]">
                    Score: {agent.composite_score}
                  </Badge>
                </div>
              </div>
            ))}
            {topAgents.length === 0 && (
              <p className="text-center text-gray-500 py-4">Nessun agente verificato trovato</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Trends Summary */}
      {trends && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Trend Ultimi 30 Giorni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500">Nuove Registrazioni</p>
                <p className="text-2xl font-bold">{getTrendTotal(trends.daily_registrations)}</p>
                {getTrendChange(trends.daily_registrations) !== 0 && (
                  <div className={`flex items-center gap-1 text-sm ${getTrendChange(trends.daily_registrations) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {getTrendChange(trends.daily_registrations) > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(getTrendChange(trends.daily_registrations))}% vs settimana prec.
                  </div>
                )}
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500">Nuovi Immobili</p>
                <p className="text-2xl font-bold">{getTrendTotal(trends.daily_properties)}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500">Offerte</p>
                <p className="text-2xl font-bold">{getTrendTotal(trends.daily_bids)}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500">Proposte</p>
                <p className="text-2xl font-bold">{getTrendTotal(trends.daily_proposals)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
